import {
  Box,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  RobotoCheckbox,
  RobotoTableCell,
  RobotoTableRow,
} from "@/shared/components";
import { FileMetadataAndTopics } from "@/shared/components/FileMetadataAndTopics";
import {
  DraggableDroppable,
  DragItem,
} from "@/shared/components/visualization/DnD";
import { type FileRecord, IngestionStatus } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import {
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";

import styles from "./DatasetFileBrowser.module.css";
import { DatasetFileRow } from "./DatasetFileRow";
import { NoFilesFoundAlert } from "./NoFilesFoundAlert";
import { NoFilesUploadedAlert } from "./NoFilesUploadedAlert";
import { RenameNode } from "./RenameNode";
import { SortableColumnHeader } from "./SortableColumnHeader";

interface DatasetFileTableProps {
  currentPage: FileSystemNode[] | null;
  datasetId: string;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional — absent when `release-3d-assets` is off, and callers pass it
   * straight through, so the components that act on it handle its absence.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  isFetching: boolean;
  isPlaceholderData: boolean;
  isSearching: boolean;
  selectedItems: FileSystemNode[];
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
}

const isChecked = (selectedItems: FileSystemNode[], row: FileSystemNode) => {
  return selectedItems.some((item) => {
    if (isDirectory(row) && isDirectory(item)) {
      return item.name === row.name;
    } else if (isFile(row) && isFile(item)) {
      return item.file.file_id === row.file.file_id;
    }
    return false;
  });
};

interface RowRenameState {
  isRenaming: boolean;
  row: FileSystemNode | null;
}

export const DatasetFileTable: React.FC<DatasetFileTableProps> = ({
  currentPage,
  datasetId,
  handleDeleteRow,
  handleRowDoubleClick,
  handleActionConfirmation,
  onIngestAsAsset,
  isFetching,
  isPlaceholderData,
  isSearching,
  selectedItems,
  handleSelectRows,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const theme = useTheme();
  const { goto } = useNavigation();

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set(),
  );

  const [rowRenameState, setRowRenameState] = React.useState<RowRenameState>({
    isRenaming: false,
    row: null,
  });

  const [menuOpenRow, setMenuOpenRow] = React.useState<FileSystemNode | null>(
    null,
  );

  let tableRowContent = null;

  const isSorting = isFetching && isPlaceholderData;

  const headerChecked =
    currentPage?.length !== 0 &&
    selectedItems.length === currentPage?.length &&
    selectedItems.every((item) => {
      return currentPage?.some((row) => {
        if (isDirectory(row) && isDirectory(item)) {
          return item.name === row.name;
        } else if (isFile(row) && isFile(item)) {
          return item.file.file_id === row.file.file_id;
        }
        return false;
      });
    });

  if (currentPage && currentPage.length > 0) {
    tableRowContent = (
      <>
        {currentPage.map((row) => {
          const isRowChecked = isChecked(selectedItems, row);

          return (
            <React.Fragment key={row.name}>
              <DraggableDroppable
                as={"tr"}
                id={row.name}
                item={{ data: row, type: DragItem.TableFSNode }}
                isDroppable={isDirectory(row)}
                className={classNames(styles.trHoverable, {
                  [styles.trPointerCursor]: isDirectory(row),
                  [styles.trMenuOpen]: row.name === menuOpenRow?.name,
                })}
                isSelected={isRowChecked}
              >
                <DatasetFileRow
                  isChecked={isRowChecked}
                  isRowExpanded={expandedRows.has(row.name)}
                  row={row}
                  goToFile={() => {
                    if (!isFile(row)) {
                      return;
                    }

                    if (
                      row.file.ingestion_status !== IngestionStatus.NotIngested
                    ) {
                      goto.visualize({
                        dataSource: {
                          files: [row.file.file_id],
                        },
                      });
                    } else {
                      goto.file({
                        fileId: row.file.file_id,
                      });
                    }
                  }}
                  handleActionConfirmation={handleActionConfirmation}
                  onIngestAsAsset={onIngestAsAsset}
                  handleDeleteRow={handleDeleteRow}
                  handleRenameNode={() => {
                    setRowRenameState({
                      isRenaming: true,
                      row,
                    });
                  }}
                  handleRowDoubleClick={handleRowDoubleClick}
                  handleSelectRows={handleSelectRows}
                  onMenuOpen={() => {
                    setMenuOpenRow(row);
                  }}
                  onMenuClose={() => {
                    setMenuOpenRow(null);
                  }}
                  toggleCollapse={() => {
                    setExpandedRows((prev) => {
                      const newState = new Set(prev);

                      if (newState.has(row.name)) {
                        newState.delete(row.name);
                      } else {
                        newState.add(row.name);
                      }
                      return newState;
                    });
                  }}
                />
              </DraggableDroppable>

              {isFile(row) && (
                <RobotoTableRow>
                  <RobotoTableCell
                    style={{
                      paddingBottom: 0,
                      paddingTop: 0,
                      borderWidth: expandedRows.has(row.name) ? "1px" : 0,
                    }}
                    colSpan={6}
                  >
                    <Collapse
                      in={expandedRows.has(row.name)}
                      mountOnEnter
                      unmountOnExit
                    >
                      <FileMetadataAndTopics
                        fileId={row.file.file_id}
                        fileVersion={row.file.version}
                        resourceOwnerId={row.file.org_id}
                      />
                    </Collapse>
                  </RobotoTableCell>
                </RobotoTableRow>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  } else if (currentPage && currentPage.length === 0) {
    tableRowContent = (
      <RobotoTableRow>
        <RobotoTableCell
          style={{
            paddingBottom: theme.spacing(2),
            paddingTop: theme.spacing(2),
          }}
          colSpan={6}
        >
          {isSearching ? (
            <NoFilesFoundAlert />
          ) : (
            <NoFilesUploadedAlert datasetId={datasetId} />
          )}
        </RobotoTableCell>
      </RobotoTableRow>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "50px",
      }}
    >
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 650 }} aria-label="files-table">
          <TableHead>
            <TableRow>
              <RobotoTableCell
                sx={{
                  padding: "0px",
                  width: "42.5px",
                  maxWidth: "42.5px",
                }}
              >
                <RobotoCheckbox
                  sx={{
                    ml: theme.spacing(1),
                    display: {
                      xs: "none",
                      sm: "inline-flex",
                    },
                  }}
                  checked={headerChecked}
                  onChange={(event) => {
                    if (currentPage) {
                      if (event.target.checked) {
                        handleSelectRows(currentPage, true);
                      } else {
                        handleSelectRows(currentPage, false);
                      }
                    }
                  }}
                  color="default"
                  inputProps={{ "aria-label": "checkbox" }}
                />
              </RobotoTableCell>

              <SortableColumnHeader
                columnName="Name"
                columnKey="name"
                sortDirection={sortDirection}
                isActive={sortColumn === "name"}
                isFetching={isSorting}
                onSort={onSort}
              />
              <SortableColumnHeader
                columnName="Last Modified"
                columnKey="modified"
                sortDirection={sortDirection}
                isActive={sortColumn === "modified"}
                isFetching={isSorting}
                onSort={onSort}
              />
              <SortableColumnHeader
                columnName="Source"
                columnKey="created_by"
                sortDirection={sortDirection}
                isActive={sortColumn === "created_by"}
                isFetching={isSorting}
                onSort={onSort}
              />
              <SortableColumnHeader
                columnName="Size"
                columnKey="size"
                sortDirection={sortDirection}
                isActive={sortColumn === "size"}
                isFetching={isSorting}
                onSort={onSort}
              />
              <RobotoTableCell>Operations</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableRowContent}</TableBody>
        </Table>
      </TableContainer>
      <RenameNode
        node={rowRenameState.row}
        handleClose={() => {
          setRowRenameState({
            isRenaming: false,
            row: null,
          });
        }}
        open={rowRenameState.isRenaming}
      />
    </Box>
  );
};
